import React from 'react';
import { FadeIn } from './FadeIn';
import { Trophy, ArrowRight, Star } from 'lucide-react';
import { motion } from 'framer-motion';
const CHECKOUT = {
  fba: 'https://43skvt-1y.myshopify.com/products/como-vender-en-amazon-fba-desde-cero-2026-bonos-exclusivos',
  fbm: 'https://43skvt-1y.myshopify.com/products/aprende-a-vender-en-amazon-fbm-desde-cero',
  sistema: 'https://43skvt-1y.myshopify.com/products/sistema-completo-de-ventas-en-amazon-fba-fbm-2026',
  wholesale: 'https://43skvt-1y.myshopify.com/products/como-vender-en-amazon-fba-wholesale-desde-cero-2026'
};
function openCheckout(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
interface OfferButtonProps {
  title: string;
  description: string;
  cta: string;
  url: string;
  featured?: boolean;
}
function OfferButton({
  title,
  description,
  cta,
  url,
  featured = false
}: OfferButtonProps) {
  return <motion.button whileHover={{
    scale: 1.015
  }} whileTap={{
    scale: 0.985
  }} onClick={() => openCheckout(url)} className={`group flex w-full flex-col items-center gap-3 rounded-2xl px-6 py-6 text-center transition-all duration-300 sm:flex-row sm:justify-between sm:text-left ${featured ? 'bg-brand-green text-white shadow-2xl shadow-brand-green/30 ring-2 ring-brand-gold/50' : 'border border-brand-text/10 bg-brand-white text-brand-text shadow-sm hover:border-brand-gold/40 hover:shadow-md'}`} data-id="element-357">
      <div className="flex flex-col sm:pr-6" data-id="element-358">
        <span className={`flex items-center justify-center gap-1.5 font-serif text-lg font-bold sm:justify-start ${featured ? 'text-white' : 'text-brand-green'}`} data-id="element-359">
          {featured && <Star className="h-4 w-4 flex-shrink-0 fill-brand-gold text-brand-gold" data-id="element-360" />}
          {title}
        </span>
        <span className={`mt-1 text-sm leading-relaxed ${featured ? 'text-white/80' : 'text-brand-text/60'}`} data-id="element-361">
          {description}
        </span>
      </div>

      <span className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide transition-colors ${featured ? 'bg-brand-gold text-brand-green group-hover:bg-brand-gold/90' : 'bg-brand-green text-white group-hover:bg-brand-green/90'}`} data-id="element-362">
        {cta}
        <ArrowRight className="h-4 w-4" data-id="element-363" />
      </span>
    </motion.button>;
}
export function Hero() {
  return <section className="relative overflow-hidden bg-gradient-to-b from-brand-beige/40 via-brand-white to-brand-white px-5 pt-16 pb-24 sm:px-6 md:pt-24 md:pb-32" data-id="element-364">
      {/* Soft background accent */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-50" data-id="element-365">
        <div className="absolute -top-32 -right-32 h-[600px] w-[600px] rounded-full bg-brand-gold/10 blur-3xl" data-id="element-366" />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl text-center" data-id="element-367">
        {/* Eyebrow */}
        <FadeIn data-id="element-368">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full bg-brand-gold/15 px-5 py-2 text-[11px] font-bold uppercase tracking-widest text-brand-green sm:text-xs" data-id="element-369">
            <Trophy className="h-3.5 w-3.5 text-brand-gold" data-id="element-370" />
            <span data-id="element-371">
              El primer paso para comenzar a generar ingresos con Amazon
            </span>
          </div>
        </FadeIn>

        {/* Title */}
        <FadeIn delay={0.1} data-id="element-372">
          <h1 className="mb-6 font-serif text-3xl font-bold leading-[1.12] tracking-tight text-brand-green sm:text-4xl md:text-5xl" data-id="element-373">
            Deja de perder tiempo buscando cómo vender en Amazon.
            <span className="mt-3 block text-xl font-medium text-brand-text/80 sm:text-2xl md:text-3xl" data-id="element-374">
              Comienza hoy con un sistema diseñado para ayudarte a{' '}
              <span className="font-bold text-brand-gold" data-id="element-375">
                generar ingresos
              </span>{' '}
              paso a paso, incluso si empiezas completamente desde cero.
            </span>
          </h1>
        </FadeIn>

        {/* Subtitle */}
        <FadeIn delay={0.2} data-id="element-376">
          <p className="mx-auto mb-12 max-w-2xl text-base leading-relaxed text-brand-text/70 sm:text-lg" data-id="element-377">
            Miles de personas quieren vender en Amazon, pero nunca comienzan
            porque no saben cuál modelo elegir. Aquí encontrarás el sistema
            adecuado según tu objetivo, explicado de forma sencilla y paso a
            paso.
          </p>
        </FadeIn>

        {/* Main image (unchanged) */}
        <FadeIn delay={0.3} data-id="element-378">
          <div className="relative mx-auto mb-14 w-full max-w-2xl" data-id="element-379">
            <div className="absolute inset-0 scale-90 rounded-3xl bg-brand-gold/15 blur-3xl" data-id="element-380" />
            <img src="https://cdn.magicpatterns.com/uploads/91JGFMLPCxeBmQ6GN9u4tX/portada_como_vender_en_amazon_comprimida_.png" alt="Cómo Vender en Amazon FBA Desde Cero (2026) - Guía Completa" className="relative h-auto w-full rounded-2xl object-contain shadow-[0_30px_80px_-15px_rgba(0,0,0,0.35)]" data-id="element-381" />
          </div>
        </FadeIn>

        {/* Offer selector line */}
        <FadeIn delay={0.35} data-id="element-382">
          <p className="mb-8 text-lg font-semibold text-brand-green sm:text-xl" data-id="element-383">
            Elige el sistema que mejor se adapte a tus objetivos.
          </p>
        </FadeIn>

        {/* Premium offer buttons */}
        <div className="mx-auto flex max-w-2xl flex-col gap-4 text-left" data-id="element-384">
          <FadeIn delay={0.4} data-id="element-385">
            <OfferButton title="Amazon FBA" description="Aprende a vender utilizando la logística de Amazon." cta="Comprar FBA" url={CHECKOUT.fba} data-id="element-386" />
          </FadeIn>
          <FadeIn delay={0.45} data-id="element-387">
            <OfferButton title="Amazon FBM" description="Aprende a vender gestionando tus propios envíos." cta="Comprar FBM" url={CHECKOUT.fbm} data-id="element-388" />
          </FadeIn>
          <FadeIn delay={0.5} data-id="element-389">
            <OfferButton title="Sistema Completo" description="Incluye Amazon FBA + Amazon FBM + Bonos Premium." cta="Comprar Sistema Completo" url={CHECKOUT.sistema} featured data-id="element-390" />
          </FadeIn>
          <FadeIn delay={0.55} data-id="element-391">
            <OfferButton title="Amazon Wholesale" description="Aprende a vender productos de marcas reconocidas." cta="Comprar Wholesale" url={CHECKOUT.wholesale} data-id="element-392" />
          </FadeIn>
        </div>
      </div>
    </section>;
}